import type {
  DiagnosisCoverage,
  DiagnosisEvidence,
  DiagnosisFinding,
  DiagnosisRule,
  DiagnosisSeverity,
} from './contracts';
import { confidenceComponents, evidenceConfidence } from './coverage';

type FindingInput = Omit<
  DiagnosisFinding,
  'id' | 'status' | 'confidence' | 'confidenceComponents' | 'caveats' | 'basicSignalRefs'
> & {
  confidence?: number;
  caveats?: string[];
  basicSignalRefs?: string[];
};

const numberValue = (metric: { status: string; effectiveValue: unknown }): number | null =>
  metric.status === 'known' && typeof metric.effectiveValue === 'number'
    ? metric.effectiveValue
    : null;

function idFor(ruleId: string, nodes: readonly string[], edges: readonly string[]): string {
  return `${ruleId}:${[...nodes].sort().join(',')}:${[...edges].sort().join(',')}`;
}

function finding(input: FindingInput, coverage: number): DiagnosisFinding {
  const components = confidenceComponents(
    input.evidence,
    coverage,
    /AMBIGUOUS|CYCLE|PARTIAL/.test(input.ruleId),
  );
  return {
    ...input,
    id: idFor(input.ruleId, input.affectedNodeIds, input.affectedEdgeIds),
    status: 'active',
    confidence: input.confidence ?? evidenceConfidence(components),
    confidenceComponents: components,
    caveats: input.caveats ?? [],
    basicSignalRefs: input.basicSignalRefs ?? [],
  };
}

function graphEvidence(
  metric: string,
  value: number | string | null,
  explanation: string,
  refs: { nodeId?: string; edgeId?: string } = {},
): DiagnosisEvidence {
  return { metric, value, ...refs, source: 'graph', explanation };
}

function dimensionCoverage(
  coverage: DiagnosisCoverage,
  name: DiagnosisCoverage['dimensions'][number]['dimension'],
): number {
  return coverage.dimensions.find((item) => item.dimension === name)?.coverage ?? 0;
}

const dataGapRule: DiagnosisRule = {
  id: 'DIAG-DATA-001',
  name: 'Missing diagnostic inputs',
  category: 'data_quality',
  appliesTo: 'node',
  priority: 60,
  evaluate(context, coverage) {
    const result: DiagnosisFinding[] = [];
    for (const node of context.graph.nodes) {
      if (node.category === 'documentation' || node.status === 'paused') continue;
      if (node.metrics.conversion.status === 'unknown') {
        result.push(
          finding(
            {
              type: 'data_gap',
              category: 'data_quality',
              title: 'Conversion data is missing',
              summary: `${node.name} cannot be compared quantitatively because conversion is unknown.`,
              severity: 'info',
              evidence: [
                graphEvidence('conversion', null, 'No effective conversion value is available.', {
                  nodeId: node.id,
                }),
              ],
              affectedNodeIds: [node.id],
              affectedEdgeIds: [],
              ruleId: 'DIAG-DATA-MISSING-CONVERSION',
              recommendation:
                'Add entries and exits or an explicit conversion value before drawing performance conclusions.',
            },
            dimensionCoverage(coverage, 'conversion'),
          ),
        );
      }
    }
    if (context.funnelMetrics.investment.status === 'unknown') {
      result.push(
        finding(
          {
            type: 'data_gap',
            category: 'data_quality',
            title: 'Cost basis is incomplete',
            summary:
              'Financial efficiency cannot be fully evaluated without a known total investment.',
            severity: 'attention',
            evidence: [graphEvidence('investment', null, 'The aggregate investment is unknown.')],
            affectedNodeIds: [],
            affectedEdgeIds: [],
            ruleId: 'DIAG-DATA-COST-BASIS',
            recommendation: 'Review cost roles and enter missing acquisition or operating costs.',
          },
          dimensionCoverage(coverage, 'financial'),
        ),
      );
    }
    const customerNodes = context.graph.nodes.filter((node) => node.roles.includes('new_customer'));
    if (
      customerNodes.length &&
      customerNodes.every((node) => node.metrics.averageTicket.status !== 'known')
    ) {
      result.push(
        finding(
          {
            type: 'data_gap',
            category: 'data_quality',
            title: 'Average ticket is missing',
            summary:
              'Revenue projection is limited because no customer endpoint has a known average ticket.',
            severity: 'attention',
            evidence: customerNodes.map((node) =>
              graphEvidence('averageTicket', null, 'Customer endpoint has no known ticket.', {
                nodeId: node.id,
              }),
            ),
            affectedNodeIds: customerNodes.map((node) => node.id),
            affectedEdgeIds: [],
            ruleId: 'DIAG-DATA-MISSING-TICKET',
            recommendation: 'Add an average ticket to a customer or revenue stage.',
          },
          dimensionCoverage(coverage, 'financial'),
        ),
      );
    }
    return result;
  },
};

const structureRule: DiagnosisRule = {
  id: 'DIAG-STRUCT-001',
  name: 'Graph structure risks',
  category: 'structure',
  appliesTo: 'graph',
  priority: 90,
  evaluate(context, coverage) {
    const operational = context.graph.nodes.filter((node) => node.category !== 'documentation');
    const incoming = new Map(operational.map((node) => [node.id, 0]));
    const outgoing = new Map(operational.map((node) => [node.id, 0]));
    for (const edge of context.graph.edges) {
      if (incoming.has(edge.target)) incoming.set(edge.target, incoming.get(edge.target)! + 1);
      if (outgoing.has(edge.source)) outgoing.set(edge.source, outgoing.get(edge.source)! + 1);
    }
    const result: DiagnosisFinding[] = [];
    for (const node of operational) {
      const ins = incoming.get(node.id) ?? 0;
      const outs = outgoing.get(node.id) ?? 0;
      if (ins === 0 && outs === 0)
        result.push(
          finding(
            {
              type: 'risk',
              category: 'structure',
              riskCategory: 'structure',
              title: 'Orphan operational stage',
              summary: `${node.name} is disconnected from the funnel flow.`,
              severity: 'attention',
              evidence: [
                graphEvidence(
                  'degree',
                  0,
                  'The operational node has no incoming or outgoing connection.',
                  { nodeId: node.id },
                ),
              ],
              affectedNodeIds: [node.id],
              affectedEdgeIds: [],
              ruleId: 'DIAG-STRUCT-ORPHAN',
              recommendation:
                'Connect the stage or mark it as documentation if it is only explanatory.',
            },
            dimensionCoverage(coverage, 'structure'),
          ),
        );
      else if (ins > 0 && outs === 0 && !node.isTerminal)
        result.push(
          finding(
            {
              type: 'risk',
              category: 'structure',
              riskCategory: 'structure',
              title: 'Dead-end operational branch',
              summary: `${node.name} receives flow but has no represented continuation.`,
              severity: 'attention',
              evidence: [
                graphEvidence(
                  'outgoing_edges',
                  0,
                  'A non-terminal stage has incoming flow and no outgoing edge.',
                  { nodeId: node.id },
                ),
              ],
              affectedNodeIds: [node.id],
              affectedEdgeIds: [],
              ruleId: 'DIAG-STRUCT-DEAD-END',
              recommendation:
                'Add the intended next stage or clarify that this is a terminal outcome.',
            },
            dimensionCoverage(coverage, 'structure'),
          ),
        );
    }
    if (!operational.some((node) => node.type === 'sale' || node.roles.includes('new_customer'))) {
      result.push(
        finding(
          {
            type: 'risk',
            category: 'structure',
            riskCategory: 'structure',
            title: 'Customer endpoint is not represented',
            summary:
              'No sale or explicit new-customer role is present, so completion cannot be aggregated confidently.',
            severity: 'attention',
            evidence: [
              graphEvidence('customer_endpoints', 0, 'No terminal customer role was found.'),
            ],
            affectedNodeIds: [],
            affectedEdgeIds: [],
            ruleId: 'DIAG-STRUCT-NO-CUSTOMER',
            recommendation:
              'Represent the customer outcome or assign the explicit new_customer metric role.',
            caveats: ['A deliberately partial funnel may not require a customer endpoint.'],
          },
          dimensionCoverage(coverage, 'structure'),
        ),
      );
    }

    if (!operational.some((node) => node.category === 'acquisition')) {
      result.push(
        finding(
          {
            type: 'risk',
            category: 'structure',
            riskCategory: 'structure',
            title: 'Acquisition source is not represented',
            summary: 'The operational graph has no explicit acquisition-category stage.',
            severity: 'attention',
            evidence: [
              graphEvidence(
                'acquisition_stage_count',
                0,
                'No acquisition-category operational node was found.',
              ),
            ],
            affectedNodeIds: [],
            affectedEdgeIds: [],
            ruleId: 'DIAG-STRUCT-NO-ACQUISITION',
            recommendation: 'Represent the traffic or acquisition source when it is in scope.',
            caveats: ['A deliberately partial downstream funnel may omit acquisition.'],
          },
          dimensionCoverage(coverage, 'structure'),
        ),
      );
    }

    const adjacency = new Map(operational.map((node) => [node.id, [] as string[]]));
    for (const edge of context.graph.edges) adjacency.get(edge.source)?.push(edge.target);
    const visiting = new Set<string>();
    const visited = new Set<string>();
    const cyclic = new Set<string>();
    const visit = (id: string) => {
      if (visiting.has(id)) {
        cyclic.add(id);
        return;
      }
      if (visited.has(id)) return;
      visiting.add(id);
      for (const next of adjacency.get(id) ?? []) {
        if (visiting.has(next)) {
          cyclic.add(id);
          cyclic.add(next);
        } else visit(next);
      }
      visiting.delete(id);
      visited.add(id);
    };
    for (const node of operational) visit(node.id);
    const unanchored = [...cyclic].filter(
      (id) =>
        context.graph.nodes.find((node) => node.id === id)?.metrics.entries.origin !== 'manual',
    );
    if (cyclic.size && unanchored.length)
      result.push(
        finding(
          {
            type: 'risk',
            category: 'structure',
            riskCategory: 'measurement',
            title: 'Cycle needs a manual metric anchor',
            summary: 'A cyclic path cannot be propagated safely without an explicit entry anchor.',
            severity: 'attention',
            evidence: [...cyclic].map((id) =>
              graphEvidence('cycle_member', id, 'Node participates in a detected directed cycle.', {
                nodeId: id,
              }),
            ),
            affectedNodeIds: [...cyclic],
            affectedEdgeIds: context.graph.edges
              .filter((edge) => cyclic.has(edge.source) && cyclic.has(edge.target))
              .map((edge) => edge.id),
            ruleId: 'DIAG-STRUCT-CYCLE',
            recommendation:
              'Add a trusted manual entry anchor to the cycle or simplify the represented loop.',
          },
          dimensionCoverage(coverage, 'structure'),
        ),
      );
    return result;
  },
};

const flowWarningsRule: DiagnosisRule = {
  id: 'DIAG-FLOW-001',
  name: 'Metric flow consistency',
  category: 'data_quality',
  appliesTo: 'edge',
  priority: 100,
  evaluate(context, coverage) {
    const supported = new Set([
      'overallocated_flow',
      'unallocated_flow',
      'partial_merge',
      'cycle_requires_manual_anchor',
      'ambiguous_metric_role',
    ]);
    return context.graph.warnings
      .filter((warning) => supported.has(warning.code))
      .map((warning) => {
        const refs = warning.refs ?? [];
        const affectedNodeIds = refs.filter((ref) =>
          context.graph.nodes.some((node) => node.id === ref),
        );
        const affectedEdgeIds = refs.filter((ref) =>
          context.graph.edges.some((edge) => edge.id === ref),
        );
        return finding(
          {
            type: 'inconsistency',
            category: warning.code.includes('flow') ? 'conversion' : 'data_quality',
            title:
              warning.code === 'overallocated_flow'
                ? 'Flow allocation exceeds 100%'
                : warning.code === 'unallocated_flow'
                  ? 'Flow allocation is incomplete'
                  : warning.code === 'partial_merge'
                    ? 'Merged flow is partial'
                    : warning.code === 'ambiguous_metric_role'
                      ? 'Metric role is ambiguous'
                      : 'Cycle requires a metric anchor',
            summary: warning.message,
            severity: warning.code === 'overallocated_flow' ? 'critical' : 'attention',
            evidence: [
              graphEvidence(warning.code, null, warning.message, {
                ...(affectedNodeIds[0] ? { nodeId: affectedNodeIds[0] } : {}),
                ...(affectedEdgeIds[0] ? { edgeId: affectedEdgeIds[0] } : {}),
              }),
            ],
            affectedNodeIds,
            affectedEdgeIds,
            ruleId: `DIAG-FLOW-${warning.code.toUpperCase()}`,
            recommendation: 'Review the connection metrics and make the represented flow explicit.',
          },
          dimensionCoverage(coverage, 'conversion'),
        );
      });
  },
};

const divergenceRule: DiagnosisRule = {
  id: 'DIAG-DATA-DIVERGENCE',
  name: 'Manual and calculated divergence',
  category: 'data_quality',
  appliesTo: 'node',
  priority: 80,
  evaluate(context, coverage) {
    const metricKeys = [
      'entries',
      'exits',
      'conversion',
      'cost',
      'revenue',
      'cpl',
      'cac',
      'averageTicket',
    ] as const;
    const findings = context.graph.nodes.flatMap((node) =>
      metricKeys.flatMap((key) => {
        const metric = node.metrics[key];
        if (
          metric.origin !== 'manual' ||
          typeof metric.effectiveValue !== 'number' ||
          typeof metric.calculatedCandidate !== 'number'
        )
          return [];
        const difference =
          Math.abs(metric.effectiveValue - metric.calculatedCandidate) /
          Math.max(Number.EPSILON, Math.abs(metric.calculatedCandidate));
        if (difference < 0.2) return [];
        return [
          finding(
            {
              type: 'inconsistency',
              category: 'data_quality',
              title: 'Manual value differs from calculated candidate',
              summary: `${node.name} has a manual ${key} value that differs materially from the deterministic candidate.`,
              severity: 'attention',
              evidence: [
                {
                  metric: key,
                  value: metric.effectiveValue,
                  comparator: `calculated ${metric.calculatedCandidate}`,
                  threshold: 0.2,
                  nodeId: node.id,
                  source: 'manual',
                  explanation:
                    'Relative divergence is at least the documented 20% warning heuristic.',
                },
              ],
              affectedNodeIds: [node.id],
              affectedEdgeIds: [],
              ruleId: `DIAG-DATA-DIVERGENCE-${key.toUpperCase()}`,
              recommendation:
                'Review both inputs and keep the manual override only when its business basis is intentional.',
              caveats: ['This warning does not declare the manual value wrong.'],
            },
            dimensionCoverage(
              coverage,
              key === 'cost' || key === 'revenue' || key === 'cac' || key === 'averageTicket'
                ? 'financial'
                : 'conversion',
            ),
          ),
        ];
      }),
    );

    const goalPeriods = new Map<string, string[]>();
    for (const node of context.graph.nodes) {
      const goal = node.metrics.goal.effectiveValue;
      if (!goal?.period) continue;
      goalPeriods.set(goal.period, [...(goalPeriods.get(goal.period) ?? []), node.id]);
    }
    if (goalPeriods.size > 1) {
      const periods = [...goalPeriods.keys()].sort();
      findings.push(
        finding(
          {
            type: 'inconsistency',
            category: 'data_quality',
            title: 'Goal periods are inconsistent',
            summary: `Known goals use different periods: ${periods.join(', ')}.`,
            severity: 'attention',
            evidence: [...goalPeriods.entries()].map(([period, nodeIds]) =>
              graphEvidence(
                'goal_period',
                period,
                `Period ${period} is used by ${nodeIds.length} goal(s).`,
                { nodeId: nodeIds[0] },
              ),
            ),
            affectedNodeIds: [...goalPeriods.values()].flat(),
            affectedEdgeIds: [],
            ruleId: 'DIAG-DATA-GOAL-PERIOD',
            recommendation:
              'Align goal periods before comparing or aggregating target performance.',
            caveats: ['Different periods may be intentional when goals are not compared directly.'],
          },
          dimensionCoverage(coverage, 'goals'),
        ),
      );
    }

    return findings;
  },
};

const retentionRiskRule: DiagnosisRule = {
  id: 'DIAG-RET-RISK',
  name: 'Relative retention risk',
  category: 'retention',
  appliesTo: 'node',
  priority: 70,
  evaluate(context, coverage) {
    const churn = context.graph.nodes
      .filter((node) => node.type === 'churn')
      .map((node) => ({ node, rate: numberValue(node.metrics.conversion) }))
      .filter(
        (item): item is { node: (typeof context.graph.nodes)[number]; rate: number } =>
          item.rate !== null,
      );
    const renewal = context.graph.nodes
      .filter((node) => node.type === 'renewal')
      .map((node) => numberValue(node.metrics.conversion))
      .filter((value): value is number => value !== null);
    if (!churn.length || !renewal.length) return [];
    const renewalRate = renewal.reduce((sum, value) => sum + value, 0) / renewal.length;
    return churn
      .filter((item) => item.rate > renewalRate)
      .map((item) =>
        finding(
          {
            type: 'risk',
            category: 'retention',
            riskCategory: 'retention',
            title: 'Churn signal exceeds renewal signal',
            summary: `${item.node.name} has a higher known rate than the represented renewal rate.`,
            severity: 'attention',
            evidence: [
              {
                metric: 'churn_conversion',
                value: item.rate,
                comparator: `renewal ${renewalRate}`,
                nodeId: item.node.id,
                source: item.node.metrics.conversion.origin ?? 'calculated',
                explanation: 'Known churn and renewal rates are compared within this funnel.',
              },
            ],
            affectedNodeIds: [item.node.id],
            affectedEdgeIds: [],
            ruleId: 'DIAG-RET-CHURN-VS-RENEWAL',
            recommendation:
              'Investigate retention causes and validate that both metrics use comparable periods.',
            caveats: ['The graph does not establish causality or statistical significance.'],
          },
          dimensionCoverage(coverage, 'retention'),
        ),
      );
  },
};

const financialRule: DiagnosisRule = {
  id: 'DIAG-FIN-001',
  name: 'Financial risks',
  category: 'financial',
  appliesTo: 'funnel',
  priority: 120,
  evaluate(context, coverage) {
    const financialCoverage = dimensionCoverage(coverage, 'financial');
    const roi = numberValue(context.funnelMetrics.roi);
    const roas = numberValue(context.funnelMetrics.roas);
    const cac = numberValue(context.funnelMetrics.cac);
    const investment = numberValue(context.funnelMetrics.investment);
    const revenue =
      numberValue(context.funnelMetrics.revenue) ??
      numberValue(context.funnelMetrics.projectedRevenue);
    const ticket =
      context.graph.nodes
        .map((node) => numberValue(node.metrics.averageTicket))
        .find((value) => value !== null) ?? null;
    const result: DiagnosisFinding[] = [];
    if (roi !== null && roi < 0)
      result.push(
        finding(
          {
            type: 'risk',
            category: 'financial',
            riskCategory: 'financial',
            title: 'Negative ROI',
            summary: 'Known return is below the known total cost basis.',
            severity: 'critical',
            evidence: [
              {
                metric: 'roi',
                value: roi,
                comparator: '< 0',
                source: context.funnelMetrics.roi.origin ?? 'calculated',
                explanation: 'Canonical ROI result is negative.',
              },
            ],
            affectedNodeIds: [],
            affectedEdgeIds: [],
            ruleId: 'DIAG-FIN-NEGATIVE-ROI',
            recommendation:
              'Review cost drivers and the stages causing the largest evidenced losses.',
          },
          financialCoverage,
        ),
      );
    if (roas !== null && roas < 1)
      result.push(
        finding(
          {
            type: 'risk',
            category: 'financial',
            riskCategory: 'financial',
            title: 'Revenue is below media investment',
            summary: 'Known attributed revenue is below known media investment.',
            severity: financialCoverage >= 0.75 ? 'critical' : 'attention',
            evidence: [
              {
                metric: 'roas',
                value: roas,
                comparator: '< 1',
                threshold: 1,
                source: context.funnelMetrics.roas.origin ?? 'calculated',
                explanation: 'Canonical ROAS is below break-even on the represented basis.',
              },
            ],
            affectedNodeIds: [],
            affectedEdgeIds: [],
            ruleId: 'DIAG-FIN-ROAS-BELOW-ONE',
            recommendation:
              'Inspect attribution, media cost and conversion losses before changing spend.',
            caveats: [
              'ROAS alone does not represent margin, lifetime value or the full business model.',
            ],
          },
          financialCoverage,
        ),
      );
    if (cac !== null && ticket !== null && cac > ticket)
      result.push(
        finding(
          {
            type: 'risk',
            category: 'financial',
            riskCategory: 'commercial',
            title: 'CAC exceeds average ticket',
            summary:
              'Known acquisition cost per customer is higher than the represented average ticket.',
            severity: 'attention',
            evidence: [
              {
                metric: 'cac',
                value: cac,
                comparator: `> ${ticket}`,
                source: context.funnelMetrics.cac.origin ?? 'calculated',
                explanation: 'CAC is compared with a known average ticket in the same graph.',
              },
            ],
            affectedNodeIds: [],
            affectedEdgeIds: [],
            ruleId: 'DIAG-FIN-CAC-TICKET',
            recommendation: 'Review acquisition cost, close rate and customer value together.',
            caveats: ['Average ticket is not lifetime value or margin.'],
          },
          financialCoverage,
        ),
      );
    if (investment !== null && revenue !== null && revenue < investment)
      result.push(
        finding(
          {
            type: 'risk',
            category: 'financial',
            riskCategory: 'financial',
            title: 'Represented revenue is below investment',
            summary: 'The current represented revenue basis does not cover represented investment.',
            severity: 'attention',
            evidence: [
              {
                metric: 'revenue_vs_investment',
                value: revenue,
                comparator: `< ${investment}`,
                source: 'calculated',
                explanation: 'Known revenue is compared with known investment in minor units.',
              },
            ],
            affectedNodeIds: [],
            affectedEdgeIds: [],
            ruleId: 'DIAG-FIN-REVENUE-INVESTMENT',
            recommendation:
              'Validate the cost and revenue basis, then prioritize the highest-loss stage.',
          },
          financialCoverage,
        ),
      );
    return result;
  },
};

const targetRule: DiagnosisRule = {
  id: 'DIAG-GOAL-001',
  name: 'Target feasibility',
  category: 'conversion',
  appliesTo: 'funnel',
  priority: 130,
  evaluate(context, coverage) {
    if (!context.target) return [];
    if (context.target.status === 'unreachable')
      return [
        finding(
          {
            type: 'goal_gap',
            category: 'conversion',
            title: 'Target is unreachable with current known inputs',
            summary: 'Reverse planning could not find a feasible path for the selected target.',
            severity: 'critical',
            evidence: [
              {
                metric: context.target.metric,
                value: context.target.required ?? null,
                source: 'scenario',
                explanation: 'The reverse engine classified the target as unreachable.',
              },
            ],
            affectedNodeIds: [],
            affectedEdgeIds: [],
            ruleId: 'DIAG-GOAL-UNREACHABLE',
            recommendation: 'Review target size, path constraints and missing conversion inputs.',
          },
          dimensionCoverage(coverage, 'goals'),
        ),
      ];
    if (context.target.status === 'ambiguous')
      return [
        finding(
          {
            type: 'data_gap',
            category: 'structure',
            title: 'Target path is ambiguous',
            summary: 'Topology or split assumptions prevent a unique reverse requirement.',
            severity: 'attention',
            evidence: [
              {
                metric: context.target.metric,
                value: context.target.required ?? null,
                source: 'scenario',
                explanation: 'The reverse engine classified the target as ambiguous.',
              },
            ],
            affectedNodeIds: [],
            affectedEdgeIds: [],
            ruleId: 'DIAG-GOAL-AMBIGUOUS',
            recommendation:
              'Clarify split shares or metric roles before treating the gap as performance failure.',
          },
          dimensionCoverage(coverage, 'goals'),
        ),
      ];
    if (
      context.target.required !== undefined &&
      context.target.current !== undefined &&
      context.target.required > context.target.current
    ) {
      const gap = context.target.required - context.target.current;
      return [
        finding(
          {
            type: 'goal_gap',
            category: 'conversion',
            title: 'Current result is below target',
            summary: `The represented target needs ${gap.toFixed(1)} additional units.`,
            severity: gap / Math.max(1, context.target.required) >= 0.5 ? 'critical' : 'attention',
            evidence: [
              {
                metric: context.target.metric,
                value: gap,
                comparator: `required ${context.target.required}; current ${context.target.current}`,
                source: 'scenario',
                explanation: 'Gap equals required minus current.',
              },
            ],
            affectedNodeIds: [],
            affectedEdgeIds: [],
            ruleId: 'DIAG-GOAL-GAP',
            recommendation:
              'Use the advanced bottleneck evidence to identify the first constrained stage.',
          },
          dimensionCoverage(coverage, 'goals'),
        ),
      ];
    }
    return [];
  },
};

const bottleneckRule: DiagnosisRule = {
  id: 'DIAG-BOTTLENECK-001',
  name: 'Advanced relative bottleneck',
  category: 'conversion',
  appliesTo: 'node',
  priority: 150,
  evaluate(context, coverage) {
    const ranked =
      context.basicBottlenecks?.candidates.filter((candidate) => candidate.rankScore !== null) ??
      [];
    if (ranked.length < 2) return [];
    const leader = ranked[0]!;
    const second = ranked[1]!;
    if (leader.rankScore! <= second.rankScore! || leader.rankScore! < 0.5) return [];
    const node = context.graph.nodes.find((candidate) => candidate.id === leader.nodeId);
    const evidence = leader.evidence.map((item) => ({
      metric: `relative_${item.signal}`,
      value: item.value,
      nodeId: leader.nodeId,
      source: 'scenario' as const,
      explanation: `Normalized relative ${item.signal.replace('_', ' ')} signal from DF-156.`,
    }));
    return [
      finding(
        {
          type: 'bottleneck',
          category: 'conversion',
          title: 'Highest relative constraint',
          summary: `${node?.name ?? leader.nodeId} has the strongest combined loss signal among comparable stages.`,
          severity: leader.rankScore! >= 0.8 ? 'critical' : 'attention',
          evidence,
          affectedNodeIds: [leader.nodeId],
          affectedEdgeIds: [],
          ruleId: 'DIAG-BOTTLENECK-RELATIVE',
          recommendation:
            'Validate this stage and test a focused improvement before scaling upstream volume.',
          impactScore: Math.round(leader.rankScore! * 100),
          basicSignalRefs: [`DF-156:${context.basicBottlenecks!.scenarioContext}:${leader.nodeId}`],
          caveats: [
            'The score is a prioritization aid based on relative evidence, not causal certainty.',
          ],
        },
        dimensionCoverage(coverage, 'conversion'),
      ),
    ];
  },
};

const opportunityRule: DiagnosisRule = {
  id: 'DIAG-OPP-001',
  name: 'Evidence-backed opportunities',
  category: 'conversion',
  appliesTo: 'graph',
  priority: 40,
  evaluate(context, coverage) {
    const known = context.graph.nodes
      .map((node) => ({ node, conversion: numberValue(node.metrics.conversion) }))
      .filter(
        (item): item is { node: (typeof context.graph.nodes)[number]; conversion: number } =>
          item.conversion !== null,
      );
    const result: DiagnosisFinding[] = [];
    const constrained = context.basicBottlenecks?.candidates
      .filter((candidate) => candidate.rankScore !== null && candidate.targetGap !== null)
      .sort(
        (left, right) =>
          right.rankScore! - left.rankScore! || left.nodeId.localeCompare(right.nodeId),
      )[0];
    if (constrained && constrained.targetGap! > 0) {
      const constrainedNode = context.graph.nodes.find((node) => node.id === constrained.nodeId);
      result.push(
        finding(
          {
            type: 'opportunity',
            category: 'conversion',
            title: 'Focused conversion improvement is worth testing',
            summary: `${constrainedNode?.name ?? constrained.nodeId} combines the strongest relative constraint with a known target gap.`,
            severity: 'positive',
            evidence: [
              graphEvidence(
                'target_gap',
                constrained.targetGap,
                'DF-156 identified a relative constraint with a known target gap.',
                { nodeId: constrained.nodeId },
              ),
            ],
            affectedNodeIds: [constrained.nodeId],
            affectedEdgeIds: [],
            ruleId: 'DIAG-OPP-HIGH-LEVERAGE-GAP',
            recommendation:
              'Test a bounded conversion improvement at this stage and compare the scenario result.',
            caveats: ['Modeled upside is directional and does not guarantee realized growth.'],
            basicSignalRefs: [
              `DF-156:${context.basicBottlenecks!.scenarioContext}:${constrained.nodeId}`,
            ],
          },
          dimensionCoverage(coverage, 'conversion'),
        ),
      );
    }
    if (known.length >= 2) {
      const sorted = [...known].sort(
        (a, b) => b.conversion - a.conversion || a.node.id.localeCompare(b.node.id),
      );
      const strongest = sorted[0]!;
      if (strongest.conversion > sorted[1]!.conversion)
        result.push(
          finding(
            {
              type: 'opportunity',
              category: strongest.node.category === 'commercial' ? 'commercial' : 'conversion',
              title: 'Strong stage worth learning from',
              summary: `${strongest.node.name} has the strongest known conversion in this funnel and may contain reusable practices.`,
              severity: 'positive',
              evidence: [
                {
                  metric: 'conversion',
                  value: strongest.conversion,
                  nodeId: strongest.node.id,
                  comparator: `next ${sorted[1]!.conversion}`,
                  source: strongest.node.metrics.conversion.origin ?? 'calculated',
                  explanation: 'Compared only with other known stages in this funnel.',
                },
              ],
              affectedNodeIds: [strongest.node.id],
              affectedEdgeIds: [],
              ruleId: 'DIAG-OPP-STRONG-STAGE',
              recommendation:
                'Document what differs at this stage and test whether it transfers to the highest-loss stage.',
              caveats: ['Relative strength does not guarantee that a practice causes the result.'],
            },
            dimensionCoverage(coverage, 'conversion'),
          ),
        );
    }
    const types = new Set(context.graph.nodes.map((node) => node.type));
    if (
      types.has('form') &&
      types.has('sdr-seller') &&
      !types.has('automation') &&
      !types.has('crm')
    )
      result.push(
        finding(
          {
            type: 'opportunity',
            category: 'automation',
            title: 'Manual handoff may be worth testing',
            summary: 'A form-to-sales flow is represented without CRM or automation context.',
            severity: 'positive',
            evidence: [
              graphEvidence(
                'represented_types',
                'form + sdr-seller',
                'Potential repetitive handoff is visible in the graph.',
              ),
            ],
            affectedNodeIds: context.graph.nodes
              .filter((node) => node.type === 'form' || node.type === 'sdr-seller')
              .map((node) => node.id),
            affectedEdgeIds: [],
            ruleId: 'DIAG-OPP-AUTOMATION',
            recommendation:
              'Evaluate whether a lightweight routing or CRM step could improve handoff consistency.',
            caveats: [
              'Automation is optional and should be tested against the actual operating process.',
            ],
          },
          dimensionCoverage(coverage, 'structure'),
        ),
      );
    const measurementCandidates = context.graph.nodes
      .filter(
        (node) =>
          node.category !== 'documentation' &&
          numberValue(node.metrics.entries) !== null &&
          node.metrics.conversion.status === 'unknown',
      )
      .sort(
        (left, right) =>
          numberValue(right.metrics.entries)! - numberValue(left.metrics.entries)! ||
          left.id.localeCompare(right.id),
      );
    if (measurementCandidates.length) {
      const candidate = measurementCandidates[0]!;
      result.push(
        finding(
          {
            type: 'opportunity',
            category: 'data_quality',
            title: 'High-throughput stage needs conversion measurement',
            summary: `${candidate.name} has the highest known throughput among stages whose conversion is unknown.`,
            severity: 'positive',
            evidence: [
              graphEvidence(
                'entries',
                numberValue(candidate.metrics.entries),
                'Known volume makes this missing measurement comparatively relevant.',
                { nodeId: candidate.id },
              ),
            ],
            affectedNodeIds: [candidate.id],
            affectedEdgeIds: [],
            ruleId: 'DIAG-OPP-MEASUREMENT',
            recommendation:
              'Add conversion measurement here before prioritizing performance changes.',
            caveats: ['Known throughput alone does not establish business impact.'],
          },
          dimensionCoverage(coverage, 'conversion'),
        ),
      );
    }
    return result;
  },
};

const retentionRule: DiagnosisRule = {
  id: 'DIAG-RET-001',
  name: 'Retention representation',
  category: 'retention',
  appliesTo: 'graph',
  priority: 30,
  evaluate(context, coverage) {
    const hasSale = context.graph.nodes.some((node) => node.type === 'sale');
    const retention = context.graph.nodes.filter((node) => node.category === 'retention');
    if (!hasSale || retention.length) return [];
    return [
      finding(
        {
          type: 'opportunity',
          category: 'retention',
          title: 'Post-sale journey is not represented',
          summary:
            'The graph reaches a sale but does not show a post-sale, renewal, upsell or churn stage.',
          severity: 'positive',
          evidence: [
            graphEvidence(
              'retention_stage_count',
              0,
              'A sale exists and no retention-category stage is represented.',
            ),
          ],
          affectedNodeIds: context.graph.nodes
            .filter((node) => node.type === 'sale')
            .map((node) => node.id),
          affectedEdgeIds: [],
          ruleId: 'DIAG-RET-MISSING-JOURNEY',
          recommendation:
            'Consider documenting the post-sale journey if retention is relevant to this funnel.',
          caveats: ['Some one-off or partial funnels intentionally end at the sale.'],
        },
        dimensionCoverage(coverage, 'retention'),
      ),
    ];
  },
};

export const DIAGNOSIS_RULES: readonly DiagnosisRule[] = [
  flowWarningsRule,
  structureRule,
  divergenceRule,
  dataGapRule,
  financialRule,
  targetRule,
  bottleneckRule,
  opportunityRule,
  retentionRule,
  retentionRiskRule,
];

export function validateRuleRegistry(rules: readonly DiagnosisRule[] = DIAGNOSIS_RULES): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  for (const rule of rules) {
    if (!rule.id.trim()) errors.push('Rule id is missing.');
    if (ids.has(rule.id)) errors.push(`Duplicate rule id: ${rule.id}`);
    ids.add(rule.id);
    if (!rule.name.trim()) errors.push(`Rule name is missing: ${rule.id}`);
  }
  return errors;
}

export const severityRank = (severity: DiagnosisSeverity): number =>
  ({ critical: 4, attention: 3, positive: 2, info: 1 })[severity];
